/**
 * "무엇을 틀까" 고르개(`PlaybackPicker`)의 목록 계산 — 플레이리스트·스케줄·콘텐츠를 한 모양으로 늘어놓고 거른다.
 *
 * 스케줄의 평소 편성·규칙, 그룹 편성, 화면의 편성 배정·일괄 배정, 캠페인·긴급의 플레이리스트가 모두 브라우저 기본
 * `<select>` 였다(2026-10-21 까지). 이름만 한 줄로 늘어서 플레이리스트인지 콘텐츠인지는 묶음 이름표로만 갈렸고, 무엇이
 * 든 플레이리스트인지·몇 시간짜리 영상인지는 열어 봐야 알았다. 콘텐츠가 수백 개면 스크롤로 찾았다.
 */
import type { Asset, Playlist, Schedule } from '../api/client.js';

export type PickKind = 'playlist' | 'schedule' | 'asset';

export interface PickValue {
  kind: PickKind;
  id: string;
}

export interface PickRow extends PickValue {
  name: string;
  /** 이름 옆 한 줄 — 항목 수·규칙 수·형식과 길이. */
  meta: string;
  /** 콘텐츠면 형식(아이콘·미리보기에 쓴다). */
  assetType?: Asset['type'];
  /** 이미지 콘텐츠의 작은 그림. */
  thumb?: string | null;
}

export interface PickSources {
  playlists?: readonly Pick<Playlist, 'id' | 'name' | 'item_count' | 'playable_count'>[];
  schedules?: readonly Pick<Schedule, 'id' | 'name' | 'rules'>[];
  assets?: readonly Pick<Asset, 'id' | 'title' | 'type' | 'size' | 'duration_sec' | 'preview_url'>[];
}

export const KIND_LABEL: Readonly<Record<PickKind, string>> = {
  playlist: '플레이리스트',
  schedule: '스케줄',
  asset: '콘텐츠',
};

const ASSET_LABEL: Readonly<Record<Asset['type'], string>> = {
  image: '이미지',
  video: '영상',
  web: '웹',
  template: '템플릿',
  html: 'HTML',
};

function seconds(value: number): string {
  const total = Math.round(value);
  if (total < 60) return `${total}초`;
  const minutes = Math.floor(total / 60);
  return total % 60 === 0 ? `${minutes}분` : `${minutes}분 ${total % 60}초`;
}

/** 한 줄 정보 — 고르기 전에 "이게 그거 맞나" 를 가리는 데 필요한 만큼만. */
export function pickRows(sources: PickSources): PickRow[] {
  const rows: PickRow[] = [];
  for (const playlist of sources.playlists ?? []) {
    const missing = playlist.item_count - playlist.playable_count;
    rows.push({
      kind: 'playlist',
      id: playlist.id,
      name: playlist.name,
      meta:
        playlist.item_count === 0
          ? '비어 있음'
          : `항목 ${playlist.item_count}개${missing > 0 ? ` · ${missing}개 빠짐` : ''}`,
    });
  }
  for (const schedule of sources.schedules ?? []) {
    rows.push({
      kind: 'schedule',
      id: schedule.id,
      name: schedule.name,
      meta: schedule.rules.length > 0 ? `규칙 ${schedule.rules.length}개` : '규칙 없음 · 하루 종일 평소 편성',
    });
  }
  for (const asset of sources.assets ?? []) {
    const length =
      asset.type === 'video' && asset.duration_sec && asset.duration_sec > 0 ? seconds(asset.duration_sec) : null;
    rows.push({
      kind: 'asset',
      id: asset.id,
      name: asset.title,
      meta: [ASSET_LABEL[asset.type], length].filter(Boolean).join(' · '),
      assetType: asset.type,
      thumb: asset.type === 'image' ? (asset.preview_url ?? null) : null,
    });
  }
  return rows;
}

/** 고른 값의 줄 — 목록에 없으면(지웠거나 보관했다) `null`. */
export function findRow(rows: readonly PickRow[], value: PickValue | null): PickRow | null {
  if (!value) return null;
  return rows.find((row) => row.kind === value.kind && row.id === value.id) ?? null;
}

export interface PickGroup {
  kind: PickKind;
  rows: PickRow[];
  /** 거른 뒤 이 종류의 전체 수 — `rows` 는 `limit` 에서 잘릴 수 있다. */
  total: number;
}

/**
 * 검색어·종류로 거르고 종류별로 묶는다. 묶음 순서는 플레이리스트 → 스케줄 → 콘텐츠(편성을 짜는 순서).
 *
 * 묶음마다 `limit` 줄까지만 그린다 — 콘텐츠가 5,000개인 고객사에서 목록을 열 때마다 줄 5,000개를 세우지 않는다.
 * 잘린 수는 `total` 로 남겨 "검색으로 좁히세요" 를 말한다.
 */
export function groupRows(rows: readonly PickRow[], query: string, kind: PickKind | 'all', limit = 100): PickGroup[] {
  const needle = query.trim().toLocaleLowerCase('ko');
  const groups: PickGroup[] = [];
  for (const each of ['playlist', 'schedule', 'asset'] as const) {
    if (kind !== 'all' && kind !== each) continue;
    const matched = rows.filter(
      (row) => row.kind === each && (!needle || row.name.toLocaleLowerCase('ko').includes(needle)),
    );
    if (matched.length === 0) continue;
    groups.push({ kind: each, rows: matched.slice(0, limit), total: matched.length });
  }
  return groups;
}

/** 주어진 원천에 들어 있는 종류 — 둘 이상이면 고르개 위에 종류 칸을 낸다. */
export function availableKinds(rows: readonly PickRow[]): PickKind[] {
  return (['playlist', 'schedule', 'asset'] as const).filter((kind) => rows.some((row) => row.kind === kind));
}
